/**
 * Timeline Unlock Composable
 *
 * Handles the unlock logic for timelines based on prerequisites
 */
import { ref } from '@vue/composition-api';

/**
 * Mapping from timeline prerequisite keys to hadFound state keys
 */
const PREREQUISITE_MAP = {
  preExhId: 'exhibitIds',
  preRelId: 'relatedIds',
  preTimeId: 'timelineIds',
  prePosId: 'positionIds'
};

/**
 * Composable for timeline unlock logic
 * @returns {Object} Unlock state and methods
 */
export function useTimelineUnlock() {
  // Had found state for each category
  const hadFoundExhibitIds = ref([]);
  const hadFoundRelatedIds = ref([]);
  const hadFoundTimelineIds = ref([]);
  const hadFoundPositionIds = ref([]);

  /**
   * Initialize had found state from API response
   * @param {Object} hadFound - Had found data from API
   */
  const initHadFound = (hadFound) => {
    hadFoundExhibitIds.value = hadFound.exhibitIds || [];
    hadFoundRelatedIds.value = hadFound.relatedIds || [];
    hadFoundPositionIds.value = hadFound.positionIds || [];
    hadFoundTimelineIds.value = hadFound.timelineIds || [];
  };

  /**
   * Get the ref for a specific hadFound type
   * @param {string} type - The type key (e.g., 'exhibitIds')
   * @returns {Ref} The corresponding ref
   */
  const getHadFoundRef = (type) => {
    const refs = {
      exhibitIds: hadFoundExhibitIds,
      relatedIds: hadFoundRelatedIds,
      timelineIds: hadFoundTimelineIds,
      positionIds: hadFoundPositionIds
    };
    return refs[type];
  };

  /**
   * Check if a timeline is unlocked based on prerequisites
   * @param {Object} timeline - Timeline object
   * @returns {boolean} Whether the timeline is unlocked
   */
  const isUnlocked = (timeline) => {
    return Object.keys(PREREQUISITE_MAP).every(prereqKey => {
      const prereqIds = timeline[prereqKey];
      // No prerequisite = unlocked
      if (!prereqIds || prereqIds.length === 0) return true;

      const hadFoundType = PREREQUISITE_MAP[prereqKey];
      const hadFoundRef = getHadFoundRef(hadFoundType);

      // All prerequisites must be in hadFound
      return prereqIds.every(id => hadFoundRef.value.includes(id));
    });
  };

  /**
   * Update had found state when receiving WebSocket event
   * @param {string} itemType - The hadFound type key (e.g., 'hadFoundExhibitIds')
   * @param {Object} item - The found item
   */
  const updateReceivedHadFoundItem = (itemType, item) => {
    if (!item) return;

    // Map from WebSocket event type to internal type
    const typeMap = {
      hadFoundExhibitIds: hadFoundExhibitIds,
      hadFoundRelatedIds: hadFoundRelatedIds,
      hadFoundTimelineIds: hadFoundTimelineIds,
      hadFoundPositionIds: hadFoundPositionIds
    };

    const targetRef = typeMap[itemType];
    if (targetRef && !targetRef.value.includes(item.id)) {
      targetRef.value.push(item.id);
    }
  };

  return {
    // State
    hadFoundExhibitIds,
    hadFoundRelatedIds,
    hadFoundTimelineIds,
    hadFoundPositionIds,

    // Methods
    initHadFound,
    isUnlocked,
    updateReceivedHadFoundItem
  };
}
